import { useEffect, useRef, useState } from 'react'

type CascadeOption = {
  label?: string
  value?: string | number
  children?: CascadeOption[]
  [key: string]: any
}

type FieldNames = {
  value?: string
  label?: string
  children?: string
}

type Props = {
  options?: CascadeOption[]
  onChange?: (
    values: (string | number)[],
    selectedOptions: CascadeOption[]
  ) => void
  value?: (string | number)[] | any
  defaultValue?: (string | number)[] | any
  fieldNames?: FieldNames
  placeholder?: string
  disabled?: boolean
  allowClear?: boolean
  name?: string
  className?: string
  'data-testid'?: string
  // accepted for API compatibility, not used
  showSearch?: any
  getPopupContainer?: any
}

const getVal = (opt: CascadeOption, fn?: FieldNames) =>
  opt[fn?.value ?? 'value']

const getLabel = (opt: CascadeOption, fn?: FieldNames): string =>
  String(opt[fn?.label ?? 'label'] ?? '')

const getChildren = (
  opt: CascadeOption,
  fn?: FieldNames
): CascadeOption[] | undefined => opt[fn?.children ?? 'children']

const findOpt = (
  opts: CascadeOption[],
  val: string | number,
  fn?: FieldNames
) => opts.find((o) => String(getVal(o, fn)) === String(val))

const toPathArray = (val: any): (string | number)[] => {
  if (!val) {
    return []
  }
  if (Array.isArray(val)) {
    return val
  }
  return [val]
}

const resolvePathFromValues = (
  vals: (string | number)[],
  opts: CascadeOption[],
  fn?: FieldNames
): CascadeOption[] => {
  const path: CascadeOption[] = []
  let current = opts
  for (const val of vals) {
    const found = findOpt(current, val, fn)
    if (!found) {
      break
    }
    path.push(found)
    const children = getChildren(found, fn)
    if (!children?.length) {
      break
    }
    current = children
  }
  return path
}

const CascadeSelect = ({
  options = [],
  onChange,
  value,
  defaultValue,
  fieldNames,
  placeholder = 'Please select',
  disabled = false,
  className,
  'data-testid': testId,
}: Props) => {
  const initialPath = resolvePathFromValues(
    toPathArray(value ?? defaultValue),
    options,
    fieldNames
  )

  const [open, setOpen] = useState(false)
  const [selectedPath, setSelectedPath] = useState<CascadeOption[]>(initialPath)
  const [hoveredPath, setHoveredPath] = useState<CascadeOption[]>(initialPath)
  const containerRef = useRef<HTMLDivElement>(null)

  // Sync selectedPath when the controlled value changes externally (reset or initial load)
  const prevValueKeyRef = useRef(JSON.stringify(value))
  useEffect(() => {
    const key = JSON.stringify(value)
    if (prevValueKeyRef.current !== key) {
      prevValueKeyRef.current = key
      const newPath = resolvePathFromValues(
        toPathArray(value),
        options,
        fieldNames
      )
      setSelectedPath(newPath)
      if (!open) {
        setHoveredPath(newPath)
      }
    }
  }, [value, options, fieldNames, open])

  // Close panel on outside click
  useEffect(() => {
    if (!open) {
      return
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
        setHoveredPath(selectedPath)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, selectedPath])

  const displayValue =
    selectedPath.length > 0
      ? selectedPath.map((o) => getLabel(o, fieldNames)).join(' / ')
      : ''

  const handleOpen = () => {
    if (disabled) {
      return
    }
    if (!open) {
      setHoveredPath(selectedPath)
    }
    setOpen((prev) => !prev)
  }

  const handleOptionHover = (opt: CascadeOption, levelIndex: number) => {
    setHoveredPath([...hoveredPath.slice(0, levelIndex), opt])
  }

  const handleOptionClick = (opt: CascadeOption, levelIndex: number) => {
    const newPath = [...hoveredPath.slice(0, levelIndex), opt]
    setHoveredPath(newPath)
    if (!getChildren(opt, fieldNames)?.length) {
      setSelectedPath(newPath)
      onChange?.(
        newPath.map((o) => getVal(o, fieldNames)),
        newPath
      )
      setOpen(false)
    }
  }

  // Build the list of columns based on the currently hovered path
  const levels: CascadeOption[][] = [options]
  for (const opt of hoveredPath) {
    const children = getChildren(opt, fieldNames)
    if (children?.length) {
      levels.push(children)
    } else {
      break
    }
  }

  return (
    <div
      ref={containerRef}
      className={`relative ${className ?? ''}`}
      data-testid={testId}
    >
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded bg-white text-left focus:outline-none focus:border-maroon-400 disabled:opacity-50 disabled:cursor-not-allowed min-h-10"
      >
        <span className={displayValue ? 'text-gray-900' : 'text-gray-400'}>
          {displayValue || placeholder}
        </span>
        <span className="text-gray-500 text-xs ml-2 select-none">
          {open ? '∧' : '∨'}
        </span>
      </button>

      {/* Flyout panel */}
      {open && (
        <div
          className="absolute z-50 flex bg-white border border-gray-200 rounded shadow-lg mt-1"
          style={{ top: '100%', left: 0, minWidth: '100%' }}
        >
          {levels.map((levelOpts, levelIndex) => (
            <div
              key={levelIndex}
              className="border-r border-gray-100 last:border-r-0 min-w-40 py-1 overflow-y-auto max-h-70"
            >
              {levelOpts.map((opt) => {
                const optVal = String(getVal(opt, fieldNames))
                const hoveredAtLevel = hoveredPath[levelIndex]
                const isActive =
                  hoveredAtLevel !== undefined &&
                  String(getVal(hoveredAtLevel, fieldNames)) === optVal
                const hasChildren = !!getChildren(opt, fieldNames)?.length

                return (
                  <div
                    key={optVal}
                    className={`px-3 py-1.5 text-sm cursor-pointer flex items-center justify-between gap-4 ${
                      isActive
                        ? 'bg-orange-50 font-semibold text-gray-900'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onMouseEnter={() => handleOptionHover(opt, levelIndex)}
                    onClick={() => handleOptionClick(opt, levelIndex)}
                  >
                    <span>{getLabel(opt, fieldNames)}</span>
                    {hasChildren && (
                      <span className="text-gray-400 text-xs flex-shrink-0">
                        ›
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CascadeSelect
